import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Menu, Plus, Edit, Trash2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import AdminSidebar from '../../components/AdminSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    rating: 5,
    review: '',
    image_url: '',
    is_featured: false
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  const handleOpenDialog = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        customer_name: testimonial.customer_name,
        rating: testimonial.rating,
        review: testimonial.review,
        image_url: testimonial.image_url || '',
        is_featured: testimonial.is_featured
      });
    } else {
      setEditingTestimonial(null);
      setFormData({
        customer_name: '',
        rating: 5,
        review: '',
        image_url: '',
        is_featured: false
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editingTestimonial) {
        // Note: Update endpoint needs to be created
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API_URL}/api/admin/testimonials`, formData);
        toast.success('Testimonial created!');
      }
      setDialogOpen(false);
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      toast.error('Failed to save testimonial');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/testimonials/${testimonialId}`);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to delete testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AdminSidebar active="testimonials" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="admin-content">
        <div className="admin-topbar">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden">
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#0f172a]">Testimonials Management</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => handleOpenDialog()} className="bg-[#064E3B] hover:bg-[#065F46]">
              <Plus className="w-5 h-5 mr-2" />
              Add Testimonial
            </Button>
            <Button onClick={handleLogout} variant="ghost">
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
                    ))}
                  </div>
                  {testimonial.is_featured && (
                    <span className="text-xs bg-[#064E3B] text-white px-2 py-1 rounded-full">Featured</span>
                  )}
                </div>
                <p className="text-zinc-700 mb-3 line-clamp-3">{testimonial.review}</p>
                <p className="font-semibold text-[#0f172a] mb-4">{testimonial.customer_name}</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(testimonial)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(testimonial.id)} className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {testimonials.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              No testimonials yet. Click "Add Testimonial" to create one.
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTestimonial ? 'Edit Testimonial' : 'New Testimonial'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input 
                value={formData.customer_name} 
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} 
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Rating</Label>
              <Select value={String(formData.rating)} onValueChange={(value) => setFormData({ ...formData, rating: parseInt(value) })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Review</Label>
              <Textarea 
                value={formData.review} 
                onChange={(e) => setFormData({ ...formData, review: e.target.value })} 
                rows={4}
                placeholder="Share your experience..."
              />
            </div>
            <div>
              <Label>Customer Image URL (Optional)</Label>
              <Input 
                value={formData.image_url} 
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} 
                placeholder="https://..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={formData.is_featured} 
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })} 
              />
              <Label>Feature on Homepage</Label>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSave} disabled={loading} className="bg-[#064E3B]">
                {loading ? 'Saving...' : 'Save Testimonial'}
              </Button>
              <Button onClick={() => setDialogOpen(false)} variant="outline">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTestimonials;
